import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { pushSubscriptionModifyEventToDataLayer } from '../../../common/analytics.js';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { SelectedPurposeOfUseOrContact } from '../../../common/enums.js';
import { useSearchParams } from '../../../common/hooks/useSearchParams.js';
import { cancelMsg, confirmMsg, t, userMsg } from '../../../common/i18n/index.js';
import { CreateContactType } from '../../../common/utils/contactUtils.js';
import { getNormalizedDateFromDateString } from '../../../common/utils/dateUtils.js';
import { SubscriptionType } from '../../../generated/api/subscriptionType.js';
import { addNotification } from '../../../public/site/notificationStore.js';
import { CreateContactModal } from '../../CreateContactModal/CreateContactModal.js';
import {
  ScheduleChangeType,
  USER_INFO_FIELD_NAMES,
  buildChangeOwnerRequest,
  getDefaultFormValues,
  getOrCreateBillingAccount,
  loadBillingAccountsContacts,
  postChangeContractOwner,
} from '../subscriptionDetailsUtils.js';
import { getMandatoryFieldRules, shouldDisableField } from './BillingAccountUserInfoValidation.js';
import { ChangeOwnerErrorMessage } from './ChangeOwnerErrorMessage.js';
import { ScheduledChangeFields } from './ScheduledChangeFields.js';
import { SubscriptionBillingAccountFields } from './SubscriptionBillingAccountFields.js';
import { SubscriptionUserInfoFields } from './SubscriptionUserInfoFields.js';
import { createOmaLaiteLaskuRemovalSupportCase, isOmaLaiteLaskuUserChange } from './omaLaiteLaskuUtils.js';
import type { PurposeOfUseOrContact } from '../../../common/types/subscription.js';
import type { BillChannel } from '../../../generated/api/billChannel.js';
import type { BillingAccountSearchResponse } from '../../../generated/api/billingAccountSearchResponse.js';
import type { ChangeOwnerError } from '../../../generated/api/changeOwnerError.js';
import type { CompanyInfoResponse } from '../../../generated/api/companyInfoResponse.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { Subscription } from '../../../generated/api/subscription.js';
import type { SubscriptionPbxDetails } from '../../../generated/api/subscriptionPbxDetails.js';
import type { AuthenticatedUser } from '../../../public/site/authState.js';
import type { BillingAccountAndUserInfoFormValues } from '../subscriptionDetailsUtils.js';

import './BillingAccountAndUserInfoForm.scss';

interface BillingAccountAndUserInfoFormProps {
  subscription: Subscription;
  companyInfo?: CompanyInfoResponse;
  contacts?: Contact[];
  billingAccounts?: BillingAccountSearchResponse[];
  billChannels: BillChannel[];
  purposeOfUseOrContact: PurposeOfUseOrContact;
  subscriptionValidationErrors?: ChangeOwnerError;
  authenticatedUser?: AuthenticatedUser;
  pbxType?: SubscriptionPbxDetails.PbxTypeEnum;
  onCancel: () => void;
  onSuccess: () => void;
}

export const BillingAccountAndUserInfoEditForm = ({
  subscription,
  companyInfo,
  contacts,
  billingAccounts,
  billChannels,
  purposeOfUseOrContact,
  subscriptionValidationErrors,
  authenticatedUser,
  pbxType,
  onCancel,
  onSuccess,
}: BillingAccountAndUserInfoFormProps) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const [targetAccountId, setTargetAccountId] = useState(mdmId);
  const [targetCompanyBillingAccounts, setTargetCompanyBillingAccounts] = useState<BillingAccountSearchResponse[]>();
  const [targetCompanyContacts, setTargetCompanyContacts] = useState<Contact[]>();
  const [targetCompanyName, setTargetCompanyName] = useState<string>();
  const [changeOwnerError, setChangeOwnerError] = useState<ChangeOwnerError>();
  const [companyContacts, setCompanyContacts] = useState(contacts || []);

  const isRingSubscription = subscription.subscriptionType === SubscriptionType.MOBILE_PBX_END_USER_SERVICE;
  const isChangeContractOwner = !!targetAccountId && targetAccountId !== mdmId;
  const isPurposeOfUseSubscription = !subscription.subscriptionContactName && !subscription.subscriptionContactId;

  const [isSaving, setIsSaving] = useState(false);
  const [createContactModalOpen, setCreateContactModalOpen] = useState(false);
  const [createContactType, setCreateContactType] = useState<CreateContactType>();
  const [contactIdBeforeCreate, setContactIdBeforeCreate] = useState<string>();

  const methods = useForm<BillingAccountAndUserInfoFormValues>({
    mode: 'all',
    shouldFocusError: true,
    defaultValues: getDefaultFormValues(subscription, purposeOfUseOrContact, companyInfo),
  });
  const key = methods.watch('key');
  const targetBillingAccount = methods.watch('changeContractOwner.targetBillingAccount');

  // Disable directory listing fields when billing account or contract owner changes
  const isBillingAccountChanged = !!targetBillingAccount && targetBillingAccount !== subscription.billingAccountId;
  const isDirectoryListingDisabled = isChangeContractOwner || isBillingAccountChanged;

  const resetDirectoryListingFields = () => {
    methods.resetField('directoryListingMobileNumberPublicity');
    methods.resetField('directoryListingCorporateNumberPublicity');
    methods.resetField('numberPrivacyMobile');
    methods.resetField('numberPrivacyCorporate');
  };

  const { isCostCenterMandatory, isEmailMandatory, isPhoneNumberMandatory } = getMandatoryFieldRules(
    subscription,
    companyInfo
  );

  const currentMdmId = isChangeContractOwner ? targetAccountId : mdmId;
  const currentBillingAccounts = (isChangeContractOwner && targetCompanyBillingAccounts) || billingAccounts || [];

  const onSubmit = async (values: BillingAccountAndUserInfoFormValues) => {
    setIsSaving(true);
    pushSubscriptionModifyEventToDataLayer('contract owner and billing account and user info');
    try {
      if (!mdmId) {
        addNotification(t.ORRM('Submit change order failed'), 'error');
        return;
      }
      const targetMdmId = isChangeContractOwner ? targetAccountId : mdmId;
      const { billingAccountId, billingAccountDisplayId } = await getOrCreateBillingAccount(
        values,
        targetMdmId,
        currentBillingAccounts
      );
      const effectiveDate =
        values.scheduledChange?.change === ScheduleChangeType.FUTURE && values.scheduledChange.date
          ? getNormalizedDateFromDateString(values.scheduledChange.date).getTime()
          : undefined;
      const changeOwnerRequest = buildChangeOwnerRequest(values, targetMdmId, billingAccountId, effectiveDate);
      const { changeOwnerErrorResponse, commonErrorResponse } = await postChangeContractOwner(
        subscription.subscriptionId,
        changeOwnerRequest,
        mdmId
      );
      if (changeOwnerErrorResponse) {
        setChangeOwnerError(changeOwnerErrorResponse);
        return;
      } else if (commonErrorResponse) {
        addNotification(t.ORRM('Submit change order failed'), 'error');
        return;
      }

      if (isOmaLaiteLaskuUserChange(subscription, purposeOfUseOrContact, values, isChangeContractOwner)) {
        try {
          await createOmaLaiteLaskuRemovalSupportCase(
            subscription,
            billingAccountDisplayId || subscription.billingAccountDisplayId,
            mdmId
          );
        } catch {
          // The change order itself already succeeded, so only report the failed support case
          addNotification(t.QP4T('Creating the support case failed. Please contact customer service.'), 'error');
        }
      }
      addNotification(t.E8T5('Submit change order successful'), 'success');
      onSuccess();
      navigate(`?mdmId=${mdmId}`, { replace: true });
    } catch (error) {
      addNotification(t.ORRM('Submit change order failed'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBillingAccountChange = (billingAccountId: string) => {
    if (billingAccountId !== subscription.billingAccountId) {
      resetDirectoryListingFields();
    }
  };

  const handleCompanyChange = async (newMdmId: string, companyName: string) => {
    try {
      const { billingAccountsResponse, contactsResponse } = await loadBillingAccountsContacts(newMdmId);
      setTargetAccountId(newMdmId);
      setTargetCompanyName(companyName);
      setTargetCompanyBillingAccounts(billingAccountsResponse?.searchResults);
      setTargetCompanyContacts(contactsResponse);
      methods.setValue('changeContractOwner.targetBillingAccount', '');
      methods.setValue('selected', SelectedPurposeOfUseOrContact.PURPOSEOFUSE);
      for (const field of USER_INFO_FIELD_NAMES) {
        methods.setValue(field, '');
      }
      resetDirectoryListingFields();
      methods.setValue('scheduledChange.change', ScheduleChangeType.NOW);
      methods.setValue('scheduledChange.date', undefined);
      methods.setValue('key', key + 1);
    } catch {
      addNotification(t.ORRM('Failed to load company information'), 'error');
    }
  };

  const handleCancel = () => {
    methods.reset();
    setTargetAccountId(undefined);
    setChangeOwnerError(undefined);
    onCancel();
  };

  const openCreateContactModal = (contactType: CreateContactType) => {
    if (contactType === CreateContactType.SUBSCRIPTION) {
      setContactIdBeforeCreate(methods.getValues('contactId'));
    }
    setCreateContactType(contactType);
    setCreateContactModalOpen(true);
  };

  const addContactIfNew = (list: Contact[], contact: Contact): Contact[] =>
    list.some(c => c.contactId === contact.contactId) ? list : [...list, contact];

  const handleContactCreated = (contact: Contact) => {
    setCreateContactModalOpen(false);
    if (isChangeContractOwner) {
      setTargetCompanyContacts(addContactIfNew(targetCompanyContacts || [], contact));
    } else {
      setCompanyContacts(addContactIfNew(companyContacts, contact));
    }

    if (createContactType === CreateContactType.SUBSCRIPTION) {
      methods.setValue('contactId', contact.contactId!, { shouldValidate: true });
    } else if (createContactType === CreateContactType.BILLING) {
      methods.setValue('billingAccount.billingContactId', contact.contactId!, { shouldValidate: true });
    } else if (createContactType === CreateContactType.BILL_RECEIVER) {
      methods.setValue('billingAccount.billReceiverId', contact.contactId!, { shouldValidate: true });
    }
    methods.setValue('key', (key || 0) + 1);
    setCreateContactType(undefined);
  };

  const handleCreateContactModalClose = () => {
    setCreateContactModalOpen(false);
    if (createContactType === CreateContactType.SUBSCRIPTION) {
      methods.setValue('contactId', contactIdBeforeCreate, { shouldValidate: true });
      setContactIdBeforeCreate(undefined);
    }
    setCreateContactType(undefined);
    methods.setValue('key', (key || 0) + 1);
  };

  return (
    <div className="of-billingAccount-userinfo-form">
      {createContactModalOpen && (
        <CreateContactModal
          onClose={handleCreateContactModalClose}
          onContactCreated={handleContactCreated}
          mdmId={currentMdmId || ''}
        />
      )}

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <CL.Grid>
            <SubscriptionBillingAccountFields
              name="changeContractOwner.targetBillingAccount"
              billingAccounts={currentBillingAccounts}
              authenticatedUser={authenticatedUser}
              showCompanySelector={true}
              onCompanyChange={handleCompanyChange}
              disabled={shouldDisableField(changeOwnerError || subscriptionValidationErrors)}
              enableAddNewBa={!isChangeContractOwner}
              billChannels={billChannels}
              contacts={companyContacts}
              isChangeContractOwner={isChangeContractOwner}
              isPurposeOfUseSubscription={isPurposeOfUseSubscription}
              originalBillingAccountId={subscription.billingAccountId}
              onCreateBillingContact={() => openCreateContactModal(CreateContactType.BILLING)}
              onCreateBillReceiverContact={() => openCreateContactModal(CreateContactType.BILL_RECEIVER)}
              targetAccountId={targetAccountId}
              isDirectoryListingDisabled={isDirectoryListingDisabled}
              onBillingAccountChange={handleBillingAccountChange}
            />

            <ChangeOwnerErrorMessage
              error={changeOwnerError || subscriptionValidationErrors}
              search={search}
              onClose={() => setChangeOwnerError(undefined)}
            />

            {!isRingSubscription && (
              <CL.GridRow className={`${dsClass.PADDING_TOP_2}`}>
                <CL.GridCol colWidth={12}>
                  <SubscriptionUserInfoFields
                    subscription={subscription}
                    contacts={(isChangeContractOwner && targetCompanyContacts) || companyContacts}
                    companyName={isChangeContractOwner ? targetCompanyName : undefined}
                    purposeOfUseOrContact={purposeOfUseOrContact}
                    isCostCenterMandatory={isCostCenterMandatory}
                    isEmailMandatory={isEmailMandatory}
                    isPhoneNumberMandatory={isPhoneNumberMandatory}
                    pbxType={pbxType}
                    onChangeContact={resetField => {
                      resetField('directoryListingMobileNumberPublicity');
                      resetField('directoryListingCorporateNumberPublicity');
                    }}
                    onCreateContact={() => openCreateContactModal(CreateContactType.SUBSCRIPTION)}
                    directoryListingDisabled={isDirectoryListingDisabled}
                  />
                </CL.GridCol>
              </CL.GridRow>
            )}
            {isRingSubscription && (
              <CL.GridRow className="of-change-owner-accordion--data-row">
                <CL.GridCol colWidth={12}>
                  <CL.Description
                    items={[
                      {
                        title: t.LUZM(userMsg),
                        description: subscription.subscriptionPurposeOfUse,
                      },
                    ]}
                  />
                </CL.GridCol>
              </CL.GridRow>
            )}
            <CL.GridRow className={dsClass.PADDING_TOP_2}>
              <CL.GridCol colWidth={12}>
                <ScheduledChangeFields disableFutureDate={isChangeContractOwner} />
              </CL.GridCol>
            </CL.GridRow>
          </CL.Grid>

          <CL.ButtonGroup align="left" className={dsClass.PADDING_LEFT_3}>
            <CL.Button type="submit" loading={isSaving}>
              {t.QVYK(confirmMsg)}
            </CL.Button>
            <CL.Button type="reset" color="link" onClick={handleCancel} disabled={isSaving}>
              {t.B2V1(cancelMsg)}
            </CL.Button>
          </CL.ButtonGroup>
        </form>
      </FormProvider>
    </div>
  );
};
